import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowClockwise, CircleNotch, Robot, Warning } from "@phosphor-icons/react";
import { adminApi, getErrorMessage } from "@/lib/api";
import { usePolling } from "@/hooks/usePolling";
import { btnGhost } from "../ui";
import { DecisionPill, fmtDate } from "../assess/parts";
import { ConfidenceRing, Concerns, DealerQuestions, ScoreBars, band } from "./AiParts";

function Header({ a, onRun, busy }) {
  const t = a.thresholds || {};
  return (
    <div className="flex items-center gap-5 flex-wrap">
      <ConfidenceRing value={a.confidence} thresholds={t} />
      <div className="flex-1 min-w-[220px] space-y-1.5">
        <div className="text-[0.62rem] uppercase tracking-[0.16em] font-bold text-neutral-500">AI's overall confidence in this claim</div>
        <div className="flex items-center gap-2 flex-wrap"><span className="text-xs text-neutral-400">AI recommends:</span> <DecisionPill decision={a.decision} testId="wi-ai-decision" /></div>
        <p className="text-[0.7rem] text-neutral-500"><span style={{ color: band(90, t) }}>■</span> {t.confidence_auto_approve ?? 90}%+ strong ·{" "}
          <span style={{ color: band(70, t) }}>■</span> {t.confidence_further_info ?? 61}–{(t.confidence_auto_approve ?? 90) - 1}% needs a closer look ·{" "}
          <span style={{ color: band(0, t) }}>■</span> under {t.confidence_further_info ?? 61}% weak</p>
        <p className="text-[0.7rem] text-neutral-600 flex items-center gap-2">Checked {fmtDate(a.assessed_at)}
          <button onClick={onRun} disabled={busy} className="text-neutral-400 hover:text-white inline-flex items-center gap-1" data-testid="wi-ai-recheck-btn"><ArrowClockwise size={11} /> Re-check</button></p>
      </div>
    </div>
  );
}

export function AiConfidence({ claimId }) {
  const [a, setA] = useState(undefined);
  const [waiting, setWaiting] = useState(null);
  const load = useCallback(() => adminApi.get(`/admin/wi/assessments/${claimId}`).then(({ data }) => setA(data))
    .catch((e) => setA((prev) => (e?.response?.status === 404 ? null : prev ?? null))), [claimId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (waiting && a?.status === "done" && a.assessed_at !== waiting) { setWaiting(null); toast.success("AI check finished"); } }, [a, waiting]);
  usePolling(load, 5000, Boolean(waiting) || a?.status === "running");
  const run = async () => {
    try {
      const { data } = await adminApi.post(`/admin/wi/assessments/${claimId}/run`);
      if (!data.started) return toast.info(data.reason || "Already running");
      setWaiting(a?.assessed_at || "none");
      toast.success("AI check started. It takes about a minute");
    } catch (e) { toast.error(getErrorMessage(e)); }
  };
  const busy = Boolean(waiting) || a?.status === "running";
  const shell = (children) => <div className="border border-neutral-800 bg-gradient-to-br from-white/[0.03] to-transparent rounded-2xl p-5 space-y-4" data-testid="wi-ai-panel">{children}</div>;
  if (a === undefined) return shell(<div className="h-24 animate-pulse" />);
  if (busy && a?.status !== "done") return shell(<p className="text-sm text-neutral-300 flex items-center gap-2" data-testid="wi-ai-running"><CircleNotch size={16} className="animate-spin text-[#73bf44]" /> The AI is checking this claim…</p>);
  if (!a || a.status !== "done") return shell(
    <div className="flex items-center justify-between gap-4 flex-wrap" data-testid="wi-ai-none">
      <p className="text-sm text-neutral-400 flex items-center gap-2"><Robot size={18} /> {a?.status === "error" ? `The last AI check failed: ${a.error || "unknown error"}` : "The AI hasn't checked this claim yet."}</p>
      <button className={btnGhost} onClick={run} data-testid="wi-ai-run-btn"><Robot size={15} /> Run AI check</button>
    </div>,
  );
  return shell(
    <>
      <Header a={a} onRun={run} busy={busy} />
      {busy && <p className="text-xs text-[#73bf44] flex items-center gap-1.5"><CircleNotch size={12} className="animate-spin" /> Re-checking…</p>}
      {a.stale && <p className="text-xs text-amber-300 flex items-center gap-1.5" data-testid="wi-ai-stale"><Warning size={13} /> The claim changed on Monday after this check. Re-check for an up-to-date view.</p>}
      {a.ai?.summary && <p className="text-sm text-neutral-200 leading-relaxed" data-testid="wi-ai-summary">{a.ai.summary}</p>}
      <div className="grid md:grid-cols-2 gap-5">
        <ScoreBars scores={a.scores} weights={a.weights} />
        <div className="space-y-4"><Concerns a={a} /><DealerQuestions items={a.ai?.questions_for_dealer} /></div>
      </div>
    </>,
  );
}
